import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ClotureRow, ClotureStatut } from '@/lib/database.types';

export function useClotures(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['clotures', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<ClotureRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('clotures')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('date_cloture', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data;
    },
  });
}

export interface CreateClotureInput {
  entreprise_id: string;
  date_cloture: string;
  caisse_id?: string | null;
  compte_bancaire_id?: string | null;
  solde_theorique: number;
  solde_reel: number;
  observation?: string | null;
}

export function useCreateCloture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateClotureInput): Promise<ClotureRow> => {
      const { data, error } = await supabase
        .from('clotures')
        .insert({ ...input, statut: 'brouillon' as ClotureStatut })
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['clotures'] }),
  });
}

export function useUpdateClotureStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; statut: ClotureStatut }): Promise<void> => {
      const now = new Date().toISOString();
      if (input.statut === 'verrouillee') {
        await supabase.rpc('cloture_lock_operations', { p_cloture_id: input.id });
      }
      const patch = {
        statut: input.statut,
        ...(input.statut === 'validee' ? { validated_at: now } : {}),
        ...(input.statut === 'verrouillee' ? { locked_at: now } : {}),
      };
      const { error } = await supabase.from('clotures').update(patch).eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clotures'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
    },
  });
}

export function useDeleteCloture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('clotures').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['clotures'] }),
  });
}
